import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { EmptyState } from "@/components/shared/EmptyState";
import type { User } from "@/types/user";
import { getRoleDisplayName } from "@/lib/auth-utils";
import { useRoleLabelMap } from "@/features/roles/hooks/useRolesQueries";
import {
  formatOptionalDateTime,
  formatArabicDate,
  formatArabicNumber,
} from "@/lib/formatters";
import { UsersFilters } from "./UsersFilters";
import { UserRowActions } from "./UserRowActions";

interface UserTableProps {
  users: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  role?: string;
  departmentId?: string;
  isActive?: boolean;
  onRoleChange: (role: string | undefined) => void;
  onDepartmentChange: (departmentId: string | undefined) => void;
  onIsActiveChange: (isActive: boolean | undefined) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  showDepartmentFilter?: boolean;
  departments?: Array<{ id: string; nameAr: string }>;
}

function getUserDisplayName(user: User): string {
  const full = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return full || "—";
}

function buildColumns(labelMap: Record<string, string>): ColumnDef<User>[] {
  return [
  /*
   * 🔄 THE `ID` COLUMN IS GONE (2026-09-18, on request). It sat in the first column — the one the
   * eye lands on — and showed a machine identifier truncated to 140px, which nobody addresses a
   * person by: users are found by name, email or username, all of which the search already covers.
   * Removed rather than hidden behind a toggle, because a column nobody wants is not a preference.
   *
   * ⚠️ NOTHING ELSE DEPENDS ON IT BEING RENDERED. `row.original.id` is still what every action uses
   * (edit, deactivate, permissions) — the id is carried in the row, not read off the screen.
   */
  {
    id: "name",
    header: "الاسم",
    cell: ({ row }) => (
      <div className="text-right font-medium">
        {getUserDisplayName(row.original)}
      </div>
    ),
  },
  {
    accessorKey: "email",
    header: "البريد الإلكتروني",
  },
  {
    accessorKey: "username",
    header: "اسم المستخدم",
    cell: ({ row }) => (
      <span className="block text-right font-mono text-xs text-muted-foreground">
        {row.original.username || "—"}
      </span>
    ),
  },
  {
    accessorKey: "role",
    header: "الدور",
    cell: ({ row }) => (
      <Badge variant="outline" className="border-primary/20 text-primary">
        {getRoleDisplayName(row.original.role, labelMap)}
      </Badge>
    ),
  },
  {
    accessorKey: "isActive",
    header: "الحالة",
    cell: ({ row }) => {
      const active = row.getValue("isActive") as boolean;
      return (
        <StatusBadge
          status={active ? "active" : "inactive"}
          config={{
            active: {
              label: "نشط",
              className: "bg-primary/10 text-primary border-primary/20",
            },
            inactive: {
              label: "معطل",
              className: "bg-muted text-muted-foreground border-border",
            },
          }}
        />
      );
    },
  },
  {
    accessorKey: "isEmailVerified",
    header: "توثيق البريد",
    cell: ({ row }) => {
      const verified = row.getValue("isEmailVerified") as boolean;
      return (
        <Badge
          variant="outline"
          className={
            verified
              ? "border-primary/20 bg-primary/10 text-primary"
              : "border-border bg-muted text-muted-foreground"
          }
        >
          {verified ? "موثّق" : "غير موثّق"}
        </Badge>
      );
    },
  },
  {
    accessorKey: "lastLoginAt",
    header: "آخر دخول",
    cell: ({ row }) => (
      <div className="text-right text-xs text-muted-foreground">
        {formatOptionalDateTime(row.original.lastLoginAt)}
      </div>
    ),
  },
  {
    accessorKey: "createdAt",
    header: "تاريخ الإنشاء",
    cell: ({ row }) => (
      <div className="text-right text-xs text-muted-foreground">
        {formatArabicDate(new Date(row.getValue("createdAt")))}
      </div>
    ),
  },
  {
    id: "actions",
    header: "الإجراءات",
    enableHiding: false,
    cell: ({ row }) => <UserRowActions user={row.original} />,
  },
];
}

export function UserTable({
  users,
  total,
  page,
  limit,
  totalPages,
  searchInput,
  onSearchInputChange,
  role,
  departmentId,
  isActive,
  onRoleChange,
  onDepartmentChange,
  onIsActiveChange,
  onPageChange,
  onLimitChange,
  onClearFilters,
  hasActiveFilters,
  showDepartmentFilter,
  departments,
}: UserTableProps) {
  const roleLabelMap = useRoleLabelMap();
  const columns = buildColumns(roleLabelMap);
  const table = useReactTable({
    data: users,
    columns,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
  });

  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const emptyDescription = hasActiveFilters
    ? "لا توجد نتائج مطابقة للفلاتر الحالية. جرّب تعديل البحث أو مسح الفلاتر."
    : "لم تتم إضافة أي مستخدمين بعد.";

  return (
    <div className="w-full space-y-4">
      <UsersFilters
        searchInput={searchInput}
        onSearchInputChange={onSearchInputChange}
        role={role}
        departmentId={departmentId}
        isActive={isActive}
        onRoleChange={onRoleChange}
        onDepartmentChange={onDepartmentChange}
        onIsActiveChange={onIsActiveChange}
        onClearFilters={onClearFilters}
        hasActiveFilters={hasActiveFilters}
        showDepartmentFilter={showDepartmentFilter}
        departments={departments}
      />

      <div className="block space-y-4 lg:hidden">
        {table.getRowModel().rows.length ? (
          table.getRowModel().rows.map((row) => (
            <Card key={row.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <h3 className="text-right text-sm font-medium">
                    {getUserDisplayName(row.original)}
                  </h3>
                  <p className="text-right text-xs text-muted-foreground">
                    {row.original.email}
                  </p>
                  {row.original.username && (
                    <p className="text-right font-mono text-[10px] text-muted-foreground/80">
                      @{row.original.username}
                    </p>
                  )}
                </div>
                <UserRowActions user={row.original} />
              </div>
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <Badge variant="outline" className="text-[10px]">
                  {getRoleDisplayName(row.original.role, roleLabelMap)}
                </Badge>
                <StatusBadge
                  status={row.original.isActive ? "active" : "inactive"}
                  config={{
                    active: {
                      label: "نشط",
                      className: "bg-primary/10 text-primary border-primary/20 text-[10px]",
                    },
                    inactive: {
                      label: "معطل",
                      className: "bg-muted text-muted-foreground border-border text-[10px]",
                    },
                  }}
                />
              </div>
              <div className="mt-2 text-right text-[10px] text-muted-foreground">
                آخر دخول: {formatOptionalDateTime(row.original.lastLoginAt)}
              </div>
            </Card>
          ))
        ) : (
          <Card>
            <EmptyState
              icon={Users}
              title="لا توجد نتائج"
              description={emptyDescription}
            />
          </Card>
        )}
      </div>

      <Card className="hidden overflow-hidden lg:block">
        <Table>
          <TableHeader className="bg-muted/30">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="py-4 text-right font-semibold"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="hover:bg-muted/20">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-4 text-right">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columns.length} className="p-0">
                  <EmptyState
                    icon={Users}
                    title="لا توجد نتائج"
                    description={emptyDescription}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground tabular-nums">
            {formatArabicNumber(from)}–{formatArabicNumber(to)} من{" "}
            {formatArabicNumber(total)} مستخدم
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">لكل صفحة:</span>
            <Select
              value={String(limit)}
              onValueChange={(v) => onLimitChange(Number(v))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 50, 100].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label="الصفحة السابقة"
          >
            <ChevronRight className="size-4" />
          </Button>
          <span className="px-2 text-sm text-muted-foreground tabular-nums">
            {formatArabicNumber(page)} / {formatArabicNumber(totalPages || 1)}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            aria-label="الصفحة التالية"
          >
            <ChevronLeft className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
